<template>
  <div
    class="form-input-search"
    :class="{
      'is-invalid': state === false,
      'disabled': $attrs.disabled === '',
      [`icon-${iconPlacement}`]: true,
      [`form-input-search-${size}`]: true,
    }"
  >
    <BInputGroup :size="size">
      <BFormInput
        :value="value"
        :state="state"
        :size="size"
        v-bind="$attrs"
        type="search"
        v-on="$listeners"
      />

      <template
        v-if="iconPlacement === 'left'"
        v-slot:prepend
      >
        <SvgIcon
          name="components/search"
          width="16"
          height="16"
        />
      </template>

      <template
        v-if="iconPlacement !== 'none'"
        v-slot:append
      >
        <SvgIcon
          v-if="iconPlacement === 'right' && (!value || value.length === 0)"
          name="components/search"
          width="16"
          height="16"
        />

        <SvgIcon
          v-if="alwaysShowClose || value && value.length > 0"
          :class="$attrs.disabled !== '' ? 'clear' : undefined"
          :name="`components/${$attrs.disabled !== '' ? 'remove' : 'search'}`"
          width="16"
          height="16"
          @click="clear"
        />
      </template>
    </BInputGroup>
  </div>
</template>

<script>
import SvgIcon from 'vue-svgicon/dist/components/svgicon.common'
import {BInputGroup} from 'bootstrap-vue/src/components/input-group/input-group'
import {BFormInput} from 'bootstrap-vue/src/components/form-input/form-input'

import '../../icons/components/remove'
import '../../icons/components/search'

export default {
  components: {
    BInputGroup,
    BFormInput,
    SvgIcon,
  },
  inheritAttrs: false,
  props: {
    /**
     * The current value of the input. Result will always be a string.
     */
    value: {
      type: String,
      default: undefined,
    },

    /**
     * Set the size of the component's appearance. 'sm', 'md' (default), or 'lg'
     */
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].indexOf(value) !== -1,
    },

    /**
     * Set the placement of the search icon. 'left' or 'right' (default)
     */
    iconPlacement: {
      type: String,
      default: 'right',
      validator: (value) => ['right', 'left', 'none'].indexOf(value) !== -1,
    },

    /**
     * Controls the validation state appearance of the component. 'true' for valid, 'false' or 'null' for no validation state
     */
    isInvalid: {
      type: Boolean,
      default: false,
    },

    alwaysShowClose: {
      type: Boolean,
      default: false,
    }
  },

  computed: {
    state () {
      if (this.isInvalid) {
        return false
      } else {
        return null
      }
    },
  },

  methods: {
    clear (e) {
      if (this.$listeners && this.$listeners.input) {
        this.$listeners.input(undefined)
      }

      /**
       * Event triggered by user interaction. Emitted after the clear button is clicked
       */
      this.$emit('clear', e)
    },
  },
}
</script>

<style lang="scss">
.form-input-search {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  border: 1px solid $gray-200;
  border-radius: 2px;

  &.disabled {
    pointer-events: none;
    border-color: $gray-200;

    &,
    .form-control,
    .input-group-prepend,
    .input-group-append {
      background-color: $gray-100;
    }

    &.icon-left {
      .input-group-append {
        display: none;
      }
    }
  }

  &:hover,
  &:active {
    border-color: $gray-700;

    .input-group-prepend,
    .input-group-append {
      color: $gray-800;
    }
  }

  &:focus,
  &.focus {
    box-shadow: 0 0 0 0.125rem $cyan-500;
    outline: none;
  }

  &-sm {
    &,
    &:active,
    &:hover,
    &.disabled {
      border-color: #fff;
    }
  }

  &-lg {
    .input-group-prepend,
    .input-group-append {
      padding-top: $input-padding-x-lg !important;
      padding-bottom: $input-padding-x-lg !important;
    }

    .input-group-append {
      padding-left: 0.0625rem !important;
      padding-right: $input-padding-x-lg !important;
    }

    .input-group-prepend {
      padding-left: $input-padding-x-lg !important;
      padding-right: 0 !important;
    }

    &.icon-right {
      .form-control {
        padding-right: 1rem !important;
        padding-left: $input-padding-x-lg !important;

        &:focus {
          padding-left: $input-padding-x-lg !important;
        }
      }
    }

    &.icon-left {
      .form-control {
        padding-left: 1rem !important;

      }
    }
  }

  .form-control {
    padding: $input-padding-y $input-padding-x;

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }

    &,
    &:hover,
    &:focus {
      border: none !important;
    }

    &:focus {
      padding: $input-padding-y $input-padding-x;
    }
  }

  .input-group {
    outline: none;
  }

  .input-group-prepend,
  .input-group-append {
    padding: 0 1rem;
    align-items: center;
    justify-content: center;
    border: none;
    color: $gray-300;
    background-color: #fff;

    .svg-icon {
      &.clear {
        cursor: pointer;
        color: $gray-300;
      }
    }
  }

  .input-group-prepend {
    padding-right: 0;
  }
}
</style>
